import { useState, useEffect } from 'react'
import { ArrowRight } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { content } from '../data/content'
import { lazy, Suspense } from 'react'
import SEO from '../components/SEO'

// Lazy Load components below the fold for instant Hero rendering
const StatsGrid = lazy(() => import('../components/StatsGrid'));
const ValueProps = lazy(() => import('../components/ValueProps'));
const ServiceHighlights = lazy(() => import('../components/ServiceHighlights'));
const CaseStudies = lazy(() => import('../components/CaseStudies'));
const Ecosystem = lazy(() => import('../components/Ecosystem'));
const MediaModal = lazy(() => import('../components/MediaModal'));
const ContactForm = lazy(() => import('../components/ContactForm'));
const LatestInsights = lazy(() => import('../components/LatestInsights'));


function Home() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        if (location.state?.scrollTo === 'contact') {
            const element = document.getElementById('contact');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [location]);

    const headline = content.hero.headline;
    const headlineLines = headline.split('\n');

    return (
        <>
            <SEO
                title="Recover Missed AVOD Revenue"
                description="Go Nova Media helps media companies and creators recover missed revenue across YouTube and global AVOD platforms with 100% transparency."
                ogTitle="Most Media Companies Are Leaving Revenue on the Table"
            />
            {/* Hero Section — Static, LCP-optimised */}
            <section className="hero-standard hero">
                <div className="container" style={{ width: '95%', maxWidth: '1200px', position: 'relative' }}>

                    {/* Single Static Headline — matches skeleton exactly for perfect LCP */}
                    <div style={{ marginBottom: '32px' }}>
                        <h1 className="text-gradient">
                            {headlineLines.map((line, i) => (
                                <div key={i} style={{ width: '100%', display: 'block' }}>
                                    {line}
                                </div>
                            ))}
                        </h1>
                    </div>

                    <div className="subtitle-container" style={{ marginBottom: '32px' }}>
                        <p className="lead-description" style={{ marginBottom: '12px' }}>
                            {content.hero.subtitle.split('\n\n')[0]}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <p style={{
                                fontSize: 'clamp(1.2rem, 2.2vw, 1.45rem)',
                                fontWeight: 700,
                                color: 'white',
                                opacity: 1
                            }}>
                                {content.hero.subtitle.split('\n\n')[1]?.split('\n')[0]}
                            </p>
                            <p style={{
                                fontSize: 'clamp(1.08rem, 1.98vw, 1.3rem)',
                                fontWeight: 400,
                                color: 'rgba(255, 255, 255, 0.7)',
                                opacity: 0.7
                            }}>
                                {content.hero.subtitle.split('\n\n')[1]?.split('\n')[1]}
                            </p>
                        </div>
                    </div>

                    <div className="cta-group" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '24px' }}>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="glass-panel cta-glow-effect"
                            style={{
                                padding: '16px 48px',
                                borderRadius: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                fontSize: '1.25rem',
                                fontWeight: 800,
                                background: 'white',
                                color: 'black',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                            }}
                        >
                            Unlock Your Revenue <ArrowRight size={24} />
                        </button>
                        <div style={{
                            marginTop: '12px',
                            fontSize: '8px',
                            opacity: 0.7,
                            fontWeight: 400,
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            color: 'rgba(255, 255, 255, 0.7)'
                        }}>
                            Trusted by global content owners & media companies
                        </div>
                    </div>

                    <Suspense fallback={null}>
                        <MediaModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
                    </Suspense>
                </div>
            </section>

            {/* Below-the-fold sections — lazy loaded */}
            <Suspense fallback={<div style={{ minHeight: '400px' }} />}>
                <StatsGrid />
            </Suspense>
            <Suspense fallback={<div style={{ minHeight: '300px' }} />}>
                <LatestInsights />
            </Suspense>
            <Suspense fallback={<div style={{ minHeight: '600px' }} />}>
                <ServiceHighlights />
            </Suspense>
            <Suspense fallback={<div style={{ minHeight: '400px' }} />}>
                <ValueProps />
            </Suspense>
            <Suspense fallback={<div style={{ minHeight: '600px' }} />}>
                <CaseStudies />
            </Suspense>
            <Suspense fallback={<div style={{ minHeight: '700px' }} />}>
                <Ecosystem />
            </Suspense>
            <Suspense fallback={<div style={{ minHeight: '500px' }} />}>
                <ContactForm />
            </Suspense>
        </>
    );
}

export default Home;

